import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { SyndicateTypeEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CreateStepperLayout from 'layouts/CreateStepperLayout';

import { FormInput, FormPanel } from 'components/FormPanel';
import { TagField } from 'components/TagInput';

import { getUser } from 'utils/auth';

const CREATE_CO_SYNDICATE_MUTATION = gql(`
  mutation CreateCoSyndicate(
    $name: String!
    $tagline: String!
    $isPrivate: Boolean!
    $type: SyndicateTypeEnumType!
    $coSyndicateLeadIds: [String!]
  ) {
    createSyndicate(
      name: $name
      tagline: $tagline
      isPrivate: $isPrivate
      type: $type
      coSyndicateLeadIds: $coSyndicateLeadIds
    ) {
      id
      name
      type
      isPrivate
      status
      createdAt
      updatedAt
    }
  }
`);

const CreateCoSyndicate: FC = () => {
  const navigate = useNavigate();
  const currentUser = getUser();

  const [coSyndicateLeadEmails, setCoSyndicateLeadEmails] = useState<Array<string>>([]);

  const [createCoSyndicate, { loading, error }] = useMutation(CREATE_CO_SYNDICATE_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        id: cache.identify({
          id: currentUser.id, // The ID of the dashboard is the same as the user ID
          __typename: 'DashboardType',
        }),
        fields: {
          syndicates(existingSyndicatesRef) {
            const newSyndicateNodeRef = cache.writeFragment({
              data: data.createSyndicate,
              fragment: gql(`
                fragment NewCoSyndicate on SyndicateType {
                  id
                  name
                  type
                  isPrivate
                  status
                  createdAt
                  updatedAt
                }
              `),
            });
            const updatedSyndicatesRef = { ...existingSyndicatesRef };
            updatedSyndicatesRef.nodes = [newSyndicateNodeRef, ...updatedSyndicatesRef.nodes];
            return updatedSyndicatesRef;
          },
        },
      });
    },
  });

  const renderContent = () => {
    return (
      <FormPanel
        loading={loading}
        error={error}
        onSubmit={data => {
          if (!coSyndicateLeadEmails.length) return;

          createCoSyndicate({
            variables: {
              name: data.name,
              tagline: data.tagline,
              isPrivate: data.isPrivate === 'true',
              type: SyndicateTypeEnumType.CoSyndicate,
              coSyndicateLeadIds: coSyndicateLeadEmails,
            },
          }).then(data => navigate(`/syndicate-dashboard/${data.data?.createSyndicate?.id}`));
        }}
      >
        <FormInput
          fieldName="name"
          type="text"
          defaultValue=""
          label="Syndicate Name *"
          placeholder="Example: AngelList India Syndicate"
          validators={{ required: true, minLength: 2 }}
        />
        <FormInput
          fieldName="tagline"
          type="text"
          defaultValue=""
          placeholder='Example: "Market access and infrastructure for investing in top-tier, venture-backed companies in India."'
          label="Syndicate Tagline *"
          helperText="One-liner describing your syndicate to LPs."
          fullWidth
          validators={{
            required: true,
            maxLength: 255,
            minLength: 10,
          }}
        />
        <FormInput
          fullWidth
          type="radio-group"
          fieldName="isPrivate"
          label="Syndicate Profile Privacy *"
          helperText="Do you want your syndicate profile to be public or private on AngelList India?"
          defaultValue={false}
          options={[
            {
              label: 'Public: Investors can discover and apply to back your syndicate.',
              value: 'false',
            },
            {
              label: 'Private: Only people with a direct link can view your profile.',
              value: 'true',
            },
          ]}
          validators={{
            required: true,
          }}
        />
        <div className="mt-4 col-span-full">
          <h3 className="text-base font-bold">Invite co-syndicate leads</h3>
          <p className="text-sm text-gray-500 mb-3">
            Enter emails of GPs you want to invite to lead this co-syndicate. They will be able to
            invite their own LPs to join the syndicate.
          </p>
          <TagField
            onTagsChange={coSyndicateLeadEmails => setCoSyndicateLeadEmails(coSyndicateLeadEmails)}
          />
        </div>
      </FormPanel>
    );
  };

  return (
    <CreateStepperLayout onClose={() => navigate('/invest/syndicates/backed')}>
      <h2 className="mt-6 text-3xl font-bold leading-9 tracking-tight text-gray-700">
        Create a Co-Syndicate
      </h2>
      <p className="text-md mt-1 text-gray-500">
        Start investing in startups alongside other investors by creating your own syndicate.
      </p>
      <div className="lg:-mx-4 my-4 p-4 bg-white ring-1 ring-gray-200 rounded-lg">
        {renderContent()}
      </div>
    </CreateStepperLayout>
  );
};

export default CreateCoSyndicate;
